export const EVENT_STATES = ["处理中", "待审批", "已结束", "已取消"];
export const TASK_STATES = ["进行中", "已结束", "已取消"];
export const permissions = [
    "dashboard.read", "duty.page", "duty.read", "duty.detail", "duty.write", "event.read", "event.write", "event.approve",
    "task.read", "task.write", "risk.read", "risk.write",
    "file.read", "file.write", "notice.write", "report.read",
    "settings.read", "settings.write", "audit.read"
];
export const defaultLogTypes = [
    { name: "普通", fields: [] },
    { name: "充值", fields: ["币种", "地址", "Hash", "链", "数量", "备注"].map((label, i) => ({ key: `f${i}`, label, kind: "text" })) },
    { name: "提现", fields: ["币种", "地址", "Hash", "链", "数量", "备注"].map((label, i) => ({ key: `f${i}`, label, kind: "text" })) },
    { name: "风控", fields: [
            { key: "exchange", label: "交易所", kind: "exchange" },
            { key: "reason", label: "风控原因", kind: "text" },
            { key: "remark", label: "备注", kind: "textarea" }
        ] }
];
// 分隔符必须是 PostgreSQL 能存进 text/jsonb 的字符：\u0000（NUL）会被 Postgres 拒绝
// （22P05 unsupported Unicode escape sequence），整条工资记录写不进库 —— 线上实测踩到过。
// 用 \u001f（Unit Separator）：Excel 表头里不会出现，JSON 里会被转义成 \u001f，Postgres 接受。
const PAYROLL_KEY_SEPARATOR = "\u001f";
/** 去掉控制字符：Postgres 存不了 NUL，Excel/粘贴内容里也可能夹带其它控制符。 */
export function stripControlChars(value) {
    // eslint-disable-next-line no-control-regex
    return String(value ?? "").replace(/[\u0000-\u001f\u007f]/g, "");
}
export function payrollFieldKey(sectionIndex, field) {
    return `${sectionIndex}${PAYROLL_KEY_SEPARATOR}${stripControlChars(field)}`;
}
/** 读取某分栏下某字段的值；旧记录只有按字段名的扁平键，作为兜底继续可读。 */
export function payrollValue(values, sectionIndex, field) {
    if (!values)
        return "";
    const composite = values[payrollFieldKey(sectionIndex, field)];
    if (composite !== undefined)
        return composite;
    return values[field] ?? "";
}
/** 老记录没有分栏结构时，合成出来的分栏名。 */
export const PAYROLL_FALLBACK_SECTION = "工资明细";
/**
 * 归一化分栏结构。三种历史形态都要能显示出内容：
 *   1. sections 正常（新记录，落库在 payrolls.sections）；
 *   2. 只有扁平的 fields 数组（最早期的记录，值也按字段名扁平存）；
 *   3. sections 丢了、但 values 里还留着「分栏序号 + 字段」组合键（经历过 sections 没落库那段时间的记录）。
 * 第 3 种只能按序号重建、字段顺序无法还原（jsonb 不保留键顺序），属于尽量救回的兜底。
 */
export function normalizePayrollSections(record, values) {
    const source = (record ?? {});
    const raw = Array.isArray(source.sections) ? source.sections : [];
    const sections = raw
        .map(item => {
        const row = (item ?? {});
        const fields = uniqueFields(Array.isArray(row.fields) ? row.fields.map(field => stripControlChars(field).trim()) : []);
        return { name: stripControlChars(row.name).trim() || PAYROLL_FALLBACK_SECTION, fields };
    })
        .filter(section => section.fields.length > 0);
    if (sections.length)
        return sections;
    const flat = uniqueFields(Array.isArray(source.fields) ? source.fields.map(field => stripControlChars(field).trim()) : []);
    if (flat.length)
        return [{ name: PAYROLL_FALLBACK_SECTION, fields: flat }];
    return sectionsFromValueKeys(values);
}
/** 从「分栏序号 + 字段」组合键里重建分栏（sections 丢失时的最后一层兜底）。 */
function sectionsFromValueKeys(values) {
    const grouped = new Map();
    for (const key of Object.keys(values ?? {})) {
        const at = key.indexOf(PAYROLL_KEY_SEPARATOR);
        if (at <= 0)
            continue;
        const index = Number(key.slice(0, at));
        const field = key.slice(at + 1).trim();
        if (!Number.isInteger(index) || index < 0 || !field)
            continue;
        grouped.set(index, [...(grouped.get(index) ?? []), field]);
    }
    return [...grouped.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([, fields]) => ({ name: PAYROLL_FALLBACK_SECTION, fields: uniqueFields(fields) }))
        .filter(section => section.fields.length > 0);
}
function uniqueFields(fields) {
    return [...new Set(fields.filter(Boolean))];
}
/**
 * 工资条里算"没有内容"的值：空、0（含 0.00 / ￥0）、-（含 — – －）。
 * 这类字段整行不显示 —— 工资表里大量 0 和 - 会把工资条塞满没有信息的行。
 */
export function blankPayrollValue(value) {
    const text = String(value ?? "").trim();
    if (!text)
        return true;
    if (/^[-—–－]+$/.test(text))
        return true;
    const numeric = text.replace(/[,\s¥￥]/g, "");
    if (/^[-+]?\d+(\.\d+)?$/.test(numeric) && Number(numeric) === 0)
        return true;
    return false;
}
/**
 * 工资条要显示的数据行：值为空、0 或 - 的字段整行不显示（用户要求），
 * 屏幕上的工资条与生成的工资图共用这一套规则，保证"屏幕所见 = 图片所见"。
 */
export function payslipEntries(values, sectionIndex, fields) {
    return fields
        .map(field => ({ field, value: payrollValue(values, sectionIndex, field).trim() }))
        .filter(entry => !blankPayrollValue(entry.value));
}
/**
 * 过滤掉没有数据的分栏与字段：整栏都空 → 该栏不显示；某字段值为空 → 该行不显示。
 * 返回的分栏 index 仍是原始序号，取值必须用它（组合键按原始序号存的）。
 */
export function visiblePayrollSections(sections, values) {
    return sections
        .map((section, index) => ({ section, index, entries: payslipEntries(values, index, section.fields) }))
        .filter(item => item.entries.length > 0);
}
// ===== 金额：一律用整数「分」，显示时再转元 =====
/** 解析用户输入的金额为「分」：接受 1,200 / 1200.5 / ￥1200；非法返回 NaN。 */
export function parseAmountToCents(value) {
    const raw = String(value ?? "").replace(/[,\s￥¥]/g, "").trim();
    if (!raw)
        return NaN;
    if (!/^-?\d+(\.\d{1,2})?$/.test(raw))
        return NaN;
    return Math.round(Number(raw) * 100);
}
export function centsToYuan(cents) {
    return Math.round(Number(cents) || 0) / 100;
}
/** 金额显示：¥1,200.00（带千分位，两位小数）。 */
export function formatCents(cents, withSymbol = true) {
    const value = centsToYuan(Number(cents) || 0);
    const text = value.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return withSymbol ? `¥${text}` : text;
}
// 风险事件页面已下线、并入任务中心：这里不再有 events 这一项（历史事件在任务上看关联事件），
// 但 event.* 权限码与 /api/events 接口都保留；配了 events 的老数据由 homePathOf 的回落逻辑兜住。
export const HOME_PAGES = [
    { key: "dashboard", label: "工作台", path: "/dashboard", permission: "dashboard.page" },
    { key: "tasks", label: "任务中心", path: "/tasks", permission: "task.page" },
    { key: "approvals", label: "审批", path: "/approvals", permission: "approval.page" },
    { key: "audit", label: "操作日志", path: "/audit", permission: "audit.page" },
    { key: "notices", label: "通知", path: "/notices", permission: ["telegram.chat.page", "telegram.group.page", "telegram.broadcast.page", "telegram.command.page", "notice.page"] },
    { key: "files", label: "文件管理", path: "/files", permission: "file.page" },
    { key: "risk", label: "风控中心", path: "/risk", permission: "risk.page" },
    {
        key: "office",
        label: "综合办公",
        path: "/office",
        permission: ["office.overview.page", "office.expense.page", "office.purchase.page", "office.asset.page", "office.attendance.page", "office.calendar.page", "office.payroll.page"]
    },
    // 统计报表暂时下线（要重新设计），页面代码保留：菜单入口、默认首页选项都不再出现，
    // 直接访问 /reports 只看到一个"暂时下线"的提示。
    { key: "reports", label: "统计报表", path: "/reports", permission: "report.page", hidden: true },
    { key: "settings", label: "系统配置", path: "/settings", permission: ["settings.page", "user.page", "role.page", "department.page", "permission.page"] }
];
/** 默认首页：工作台 */
export const DEFAULT_HOME = "dashboard";
export function homePageOf(key) {
    const wanted = String(key ?? "").trim();
    // 下线的页面不能被选成默认首页：返回 null，让调用方回落到别的页面
    return HOME_PAGES.find(page => page.key === wanted && !page.hidden) ?? null;
}
/** 首页 key → 路由路径；配了不存在的值一律回落到默认首页。 */
export function homePathOf(key) {
    return (homePageOf(key) ?? homePageOf(DEFAULT_HOME)).path;
}
export function homePagePermission(key) {
    return homePageOf(key)?.permission ?? "";
}
/** 预览大小上限：超过就直接提示下载（浏览器端解析，太大既卡又没意义） */
export const PREVIEW_MAX_BYTES = 20 * 1024 * 1024;
/** 文本预览更小：整篇读进内存再解码，2MB 已经能看几万行 */
export const PREVIEW_TEXT_MAX_BYTES = 2 * 1024 * 1024;
const PREVIEW_EXT = [
    [/\.(png|jpe?g|gif|webp|bmp|svg|avif|ico)$/i, "image"],
    [/\.(mp4|webm|mov|m4v|avi|mkv)$/i, "video"],
    [/\.(mp3|wav|ogg|m4a|aac|flac)$/i, "audio"],
    [/\.pdf$/i, "pdf"],
    // 老 .doc / .wps / .rtf 和 ppt 没有浏览器端解析方案，一律走下载
    [/\.(docx|docm)$/i, "docx"],
    [/\.(xlsx|xlsm|xls|csv|tsv)$/i, "sheet"],
    [/\.(txt|md|markdown|log|json|xml|ya?ml|toml|ini|conf|cfg|sh|bash|zsh|sql|py|js|mjs|cjs|ts|tsx|vue|java|kt|go|rs|c|h|cpp|hpp|cs|php|rb|swift|css|scss|less|html?)$/i, "text"]
];
/**
 * 文件该怎么预览。先看扩展名（最可靠），再退回 Content-Type。
 * 注意 `.svg` / `.html` 也算"能预览"，但前端分别用 <img> 和纯文本渲染，代理接口也只按 text/plain 吐，
 * 不会让它们以可执行的形式在应用域名下运行。
 */
export function previewKindOf(input) {
    const name = String(input.name ?? "").trim();
    for (const [pattern, kind] of PREVIEW_EXT)
        if (pattern.test(name))
            return kind;
    const mime = String(input.contentType ?? "").trim().toLowerCase();
    if (!mime)
        return "none";
    if (mime.startsWith("image/"))
        return "image";
    if (mime.startsWith("video/"))
        return "video";
    if (mime.startsWith("audio/"))
        return "audio";
    if (mime.includes("pdf"))
        return "pdf";
    if (mime.includes("wordprocessingml.document"))
        return "docx";
    if (mime.includes("spreadsheetml") || mime.includes("ms-excel") || mime === "text/csv" || mime === "text/tab-separated-values")
        return "sheet";
    if (mime.startsWith("text/"))
        return "text";
    if (mime.includes("json") || mime.includes("xml") || mime.includes("javascript"))
        return "text";
    return "none";
}
/** 代理接口回给浏览器的 Content-Type：能解析成文本的按纯文本吐，其余一律 octet-stream（+ nosniff） */
export function previewResponseType(kind) {
    if (kind === "text")
        return "text/plain; charset=utf-8";
    if (kind === "pdf")
        return "application/pdf";
    return "application/octet-stream";
}
export * from "./permissions.js";
export * from "./time.js";
export * from "./duty.js";
export * from "./rich-text.js";
export * from "./task-stage.js";
export * from "./sheet.js";
